// readinessLearning.js — mine the objective-vs-subjective gap from history.
//
// Each day the user reports a feel (Strong/Good/Off/Drained) it's stored with a
// context snapshot (objective score/band, sleep, HRV delta, recovery hours,
// leg-training recency). Over time that lets us answer: "when do you feel worse
// than the sensors say, and what tends to explain it?" — so the card can warn
// you before you hit a wall instead of after.

const BAND_ORDER = ['REST', 'EASY', 'MEDIUM', 'HARD'];
const FEEL_CAP = { strong: null, good: null, off: 'MEDIUM', drained: 'REST' };

// Did this feel pull the recommendation below the objective band?
function feltBelow(entry) {
  const cap = FEEL_CAP[entry.feel];
  if (!cap) return false;
  const obj = entry.feelCtx?.objBand || entry.scoreBand || null;
  if (!obj) return false;
  return BAND_ORDER.indexOf(cap) < BAND_ORDER.indexOf(obj);
}

const pct = (num, den) => den ? Math.round((num / den) * 100) : 0;

// Returns { ready, count, belowRate, topPattern, summary } or { ready:false }.
export function analyzeFeelGap(history) {
  const paired = (history || []).filter(e => e.feel && e.feelCtx);
  if (paired.length < 5) return { ready: false, count: paired.length };

  const belowDays = paired.filter(feltBelow);
  const belowRate = pct(belowDays.length, paired.length);

  // Among the days you felt worse than the sensors, what's the common thread?
  const candidates = [];
  if (belowDays.length >= 3) {
    const legs = belowDays.filter(e => e.feelCtx.legsRecentH != null && e.feelCtx.legsRecentH < 36);
    if (legs.length) candidates.push({ key: 'legs', n: legs.length, label: `the day after a leg session`, rate: pct(legs.length, belowDays.length) });
    const lowSleep = belowDays.filter(e => e.feelCtx.sleepH != null && e.feelCtx.sleepH < 7);
    if (lowSleep.length) candidates.push({ key: 'sleep', n: lowSleep.length, label: `on under-7h sleep`, rate: pct(lowSleep.length, belowDays.length) });
    const recentTrain = belowDays.filter(e => e.feelCtx.recH != null && e.feelCtx.recH < 30);
    if (recentTrain.length) candidates.push({ key: 'recent', n: recentTrain.length, label: `within ~a day of training`, rate: pct(recentTrain.length, belowDays.length) });
    const goodHrv = belowDays.filter(e => e.feelCtx.hrvDelta != null && e.feelCtx.hrvDelta > -5);
    if (goodHrv.length) candidates.push({ key: 'hrvgap', n: goodHrv.length, label: `even with HRV looking fine`, rate: pct(goodHrv.length, belowDays.length) });
  }
  candidates.sort((a, b) => b.rate - a.rate || b.n - a.n);
  const topPattern = candidates.find(c => c.rate >= 50) || null;

  let summary;
  if (belowRate >= 40) {
    summary = `You feel below your objective score on ${belowRate}% of logged days`
      + (topPattern ? ` — most often ${topPattern.label} (${topPattern.rate}%). The model tends to over-rate you there.` : `. Your read tends to run below the sensors.`);
  } else if (belowRate > 0) {
    summary = `Your feel matches the objective read most days (below it only ${belowRate}% of the time).`;
  } else {
    summary = `Your feel has tracked the objective read on every logged day so far.`;
  }

  return { ready: true, count: paired.length, belowRate, topPattern, summary };
}

// If today's context matches a learned "you usually feel below" pattern, return
// a short heads-up string; else null. Used to pre-empt before the user taps.
export function preEmptiveFeelHint(history, todayCtx) {
  const a = analyzeFeelGap(history);
  if (!a.ready || !a.topPattern || !todayCtx) return null;
  const p = a.topPattern;
  const matches =
    (p.key === 'legs'   && todayCtx.legsRecentH != null && todayCtx.legsRecentH < 36) ||
    (p.key === 'sleep'  && todayCtx.sleepH != null && todayCtx.sleepH < 7) ||
    (p.key === 'recent' && todayCtx.recH != null && todayCtx.recH < 30) ||
    (p.key === 'hrvgap' && todayCtx.hrvDelta != null && todayCtx.hrvDelta > -5);
  if (!matches) return null;
  return `Heads-up: you usually feel below your score ${p.label} — sensors may be over-rating today.`;
}
